import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FaUser, FaEnvelope, FaLock, FaVenusMars, FaHeart, FaCalendar, 
  FaMapMarkerAlt, FaFlag, FaQuoteRight, FaGlobe, FaStar, FaCamera, 
  FaArrowLeft, FaArrowRight, FaCheck, FaFire, FaShieldAlt,
  FaSpinner, FaEye, FaEyeSlash, FaSun, FaMoon, FaSnowflake
} from 'react-icons/fa';
import logo from '../../logo/logo.svg';
import api from '../../services/api';
import toast from 'react-hot-toast';

const Register = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const navigate = useNavigate();
  const avatarInputRef = useRef(null);
  
  // Images pour les slides
  const slides = [  
    'https://murima.julesferry-ga.com/uploads/92233485_054_24b5.jpg',
    'https://murima.julesferry-ga.com/uploads/13331005_034_260d.jpg',
    'https://murima.julesferry-ga.com/uploads/30599539_057_93d6.jpg',
    'https://murima.julesferry-ga.com/uploads/86489020_027_fd46.jpg'
  ];

  // Captions pour chaque slide
  const captions = [
    'L\'amour est dans l\'air',
    'Rencontres exotiques',
    'Moments magiques',
    'Vivez l\'aventure'
  ];

  // Auto slide toutes les 5 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    pseudo: '',
    prenom: '',
    nom: '',
    genre: '',
    orientation: '',
    date_naissance: '',
    ville: 'Paris',
    pays: 'France',
    bio: '',
    origine: '',
    avatar: null
  });

  const interests = [
    { id: 1, nom: 'Skinny', icon: <FaUser style={{ fontSize: '0.9rem' }} /> },
    { id: 2, nom: 'Apoutchou', icon: <FaHeart style={{ fontSize: '0.9rem' }} /> },
    { id: 3, nom: 'Grande taille', icon: <FaUser style={{ fontSize: '0.9rem' }} /> },
    { id: 4, nom: 'Court(e) taille', icon: <FaUser style={{ fontSize: '0.9rem' }} /> },
    { id: 5, nom: 'Clair(e)', icon: <FaSun style={{ fontSize: '0.9rem' }} /> },
    { id: 6, nom: 'Métisse', icon: <FaStar style={{ fontSize: '0.9rem' }} /> },
    { id: 7, nom: 'Foncé(e)', icon: <FaMoon style={{ fontSize: '0.9rem' }} /> },
    { id: 8, nom: 'Blanc(he)', icon: <FaSnowflake style={{ fontSize: '0.9rem' }} /> }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image trop volumineuse (max 5MB)');
        return;
      }
      setFormData(prev => ({ ...prev, avatar: file }));
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const toggleInterest = (interestId) => {
    setSelectedInterests(prev => 
      prev.includes(interestId) 
        ? prev.filter(id => id !== interestId)
        : [...prev, interestId]
    );
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = "L'email est requis";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Email invalide";
    if (!formData.password) newErrors.password = "Le mot de passe est requis";
    else if (formData.password.length < 6) newErrors.password = "Minimum 6 caractères";
    if (!formData.pseudo) newErrors.pseudo = "Le pseudo est requis";
    if (!formData.prenom) newErrors.prenom = "Le prénom est requis";
    if (!formData.nom) newErrors.nom = "Le nom est requis";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.genre) newErrors.genre = "Le genre est requis";
    if (!formData.orientation) newErrors.orientation = "L'orientation est requise";
    if (!formData.date_naissance) newErrors.date_naissance = "La date de naissance est requise";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => true;
  const validateStep4 = () => true;

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
    else if (step === 3 && validateStep3()) setStep(4);
    else if (step === 4 && validateStep4()) setStep(5);
  };

  const handlePrev = () => setStep(step - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step < 5) return;
    
    if (!avatarPreview && !formData.avatar) {
      toast.error('Veuillez ajouter une photo de profil');
      return;
    }
    
    setLoading(true);
    
    try {
      const submitData = new FormData();
      submitData.append('email', formData.email);
      submitData.append('password', formData.password);
      submitData.append('pseudo', formData.pseudo);
      submitData.append('prenom', formData.prenom);
      submitData.append('nom', formData.nom);
      submitData.append('genre', formData.genre);
      submitData.append('orientation', formData.orientation);
      submitData.append('date_naissance', formData.date_naissance);
      submitData.append('ville', formData.ville);
      submitData.append('pays', formData.pays);
      submitData.append('bio', formData.bio || '');
      submitData.append('origine', formData.origine || '');
      submitData.append('interests', JSON.stringify(selectedInterests));
      
      if (formData.avatar) {
        submitData.append('avatar', formData.avatar);
      }
      
      const response = await api.post('/register.php', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        toast.success('Compte créé avec succès !');
        setTimeout(() => navigate('/login'), 1500);
      } else {
        if (response.data.error === 'email_exists') {
          setErrors({ email: 'Cet email est déjà utilisé' });
          setStep(1);
          toast.error('Cet email est déjà utilisé');
        } else if (response.data.error === 'pseudo_exists') {
          setErrors({ pseudo: 'Ce pseudo est déjà utilisé' });
          setStep(1);
          toast.error('Ce pseudo est déjà utilisé');
        } else {
          toast.error(response.data.error || 'Erreur lors de l\'inscription');
        }
      }
    } catch (error) {
      console.error('Erreur inscription:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 18px 14px 48px',
    background: '#2a2a3a',
    border: '2px solid #ff4b6e',
    borderRadius: '30px',
    color: 'white',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'all 0.3s'
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh', overflow: 'hidden' }}>
      {/* Background Slides */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
        {slides.map((slide, index) => (
          <div
            key={index}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url(${slide})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: currentSlide === index ? 1 : 0,
              transition: 'opacity 1.5s ease-in-out',
              transform: 'scale(1.05)',
              animation: currentSlide === index ? 'zoomIn 20s ease infinite alternate' : 'none'
            }}
          />
        ))}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.3) 100%)',
          zIndex: 1
        }} />
        <div style={{
          position: 'absolute',
          bottom: '40px',
          left: 0,
          right: 0,
          textAlign: 'center',
          color: 'white',
          fontSize: '1rem',
          fontWeight: '300',
          letterSpacing: '2px',
          textTransform: 'uppercase',
          zIndex: 2,
          textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)',
          animation: 'fadeInUp 1s ease'
        }}>
          <p>{captions[currentSlide]}</p>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ position: 'relative', zIndex: 10, display: 'flex', width: '100%', height: '100%' }}>
        {/* Logo et Branding - Gauche (disparaît sur mobile) */}
        <div className="register-brand-left" style={{ 
  flex: 1, 
  display: 'flex', 
  alignItems: 'center', 
  justifyContent: 'center', 
  background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%)',
  backdropFilter: 'blur(10px)',
  height: '100%',
  position: 'relative',
  overflow: 'hidden'
}}>
  {/* Effet de particules en arrière-plan */}
  <div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 50% 40%, rgba(255,75,110,0.15) 0%, transparent 70%)',
    pointerEvents: 'none'
  }} />
  
  <div style={{ 
    textAlign: 'center', 
    padding: '40px',
    animation: 'fadeInUp 0.8s ease',
    position: 'relative',
    zIndex: 2
  }}>
    {/* Logo animé */}
    <div style={{
      position: 'relative',
      display: 'inline-block',
      marginBottom: '25px',
      animation: 'float 3s ease-in-out infinite'
    }}>
    <img 
           src={logo} 
           alt="MURIMA" 
           style={{ 
             width: '320px',
             filter: 'drop-shadow(0 0 20px rgba(255,75,110,0.4))',
             transition: 'all 0.3s ease'
           }} 
         />
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '150%',
        height: '150%',
        background: 'radial-gradient(circle, rgba(255,75,110,0.2) 0%, transparent 70%)',
        borderRadius: '50%',
        zIndex: -1,
        animation: 'pulse 2s ease infinite'
      }} />
    </div>
    
    {/* Titre avec dégradé */}
    <h1 style={{ 
      fontSize: '2.5rem', 
      fontWeight: '700',
      background: 'linear-gradient(135deg, #fff, #ff8a5c)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '8px',
      letterSpacing: '1px'
    }}>
      Plateforme de rencontres sensuelles
    </h1>
  
    
    {/* Ligne décorative */}
    <div style={{
      width: '60px',
      height: '2px',
      background: 'linear-gradient(90deg, #ff4b6e, #ff8a5c)',
      margin: '20px auto',
      borderRadius: '2px'
    }} />
    
    {/* Avantages avec design moderne */}
    <div style={{ 
      marginTop: '50px', 
      display: 'flex', 
      flexDirection: 'column', 
      gap: '18px',
      background: 'rgba(255,255,255,0.03)',
      padding: '30px 35px',
      borderRadius: '35px',
      backdropFilter: 'blur(5px)',
      border: '1px solid rgba(255,75,110,0.2)'
    }}>
      {[
        { text: 'Profils vérifiés', icon: '✓' },
        { text: 'Rencontres authentiques', icon: '✓' },
        { text: 'Sécurité garantie', icon: '✓' }
      ].map((item, index) => (
        <div 
          key={index}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            padding: '8px 5px',
            transition: 'all 0.3s ease',
            cursor: 'default'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateX(8px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateX(0)';
          }}
        >
          <div style={{
            width: '32px',
            height: '32px',
            background: 'linear-gradient(135deg, #ff4b6e, #ff8a5c)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 15px rgba(255,75,110,0.4)'
          }}>
            <FaCheck style={{ color: 'white', fontSize: '0.9rem' }} />
          </div>
          <span style={{ 
            color: 'rgba(255,255,255,0.9)', 
            fontSize: '1rem', 
            fontWeight: '500' 
          }}>
            {item.text}
          </span>
        </div>
      ))}
    </div>
    
    {/* Petit texte d'engagement */}
    <p style={{
      marginTop: '30px',
      color: 'rgba(255,255,255,0.4)',
      fontSize: '0.75rem',
      letterSpacing: '0.5px'
    }}>
      Rejoignez la communauté MURIMA
    </p>
  </div>

  <style>{`
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes float {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-8px);
      }
    }
    
    @keyframes pulse {
      0%, 100% {
        transform: translate(-50%, -50%) scale(1);
        opacity: 0.4;
      }
      50% {
        transform: translate(-50%, -50%) scale(1.2);
        opacity: 0.7;
      }
    }
  `}</style>
</div>

        {/* Formulaire - Droite (prend tout l'écran sur mobile) */}
      <div style={{ 
  flex: 1, 
  display: 'flex', 
  alignItems: 'center', 
  justifyContent: 'center', 
  padding: '20px', // Réduit sur mobile
  background: 'rgba(0,0,0,0.4)',
  height: '100%',
  overflow: 'auto',
  '@media (max-width: 768px)': {
     padding: '20px'
    },

    '@media (min-width: 769px)': {
      
     padding: '40px'
    }
}}>
  <div style={{ 
    width: '100%', 
    maxWidth: '520px',
    // Sur mobile, on augmente la largeur et on réduit les paddings
      // Sur mobile, prend presque tout l'écran
    background: 'rgba(20,20,40,0.95)', 
    borderRadius: '32px', 
    padding: '30px 25px', // Padding réduit sur mobile
    boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
    border: '1px solid rgba(255,75,110,0.3)',
    // Styles responsifs
    '@media (max-width: 768px)': {
      padding: '25px 20px',
      borderRadius: '24px',
      maxWidth: 'calc(100% - 20px)'
    },

    '@media (min-width: 769px)': {
      
      maxWidth: '480px'
    }
  }}>
            {/* Progress Steps - 5 steps */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '35px', padding: '0 20px' }}>
              {[1, 2, 3, 4, 5].map((s) => (
                <div key={s} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <div 
                    onClick={() => step > s && setStep(s)}
                    style={{
                      width: '40px',
                      height: '40px',
                      background: step >= s ? (step > s ? '#4CAF50' : 'linear-gradient(135deg, #ff4b6e, #ff8a5c)') : '#2a2a3a',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      color: 'white',
                      cursor: step > s ? 'pointer' : 'default',
                      border: step >= s ? 'none' : '2px solid #ff4b6e',
                      transform: step === s ? 'scale(1.05)' : 'scale(1)',
                      transition: 'all 0.3s',
                      boxShadow: step === s ? '0 0 15px rgba(255,75,110,0.5)' : 'none'
                    }}
                  >
                    {step > s ? <FaCheck /> : s}
                  </div>
                  {s < 5 && <div style={{ flex: 1, height: '2px', background: '#ff4b6e', margin: '0 8px' }}></div>}
                </div>
              ))}
            </div>

            {/* Titre de l'étape */}
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <h2 style={{ fontSize: '1.6rem', fontWeight: '600', color: 'white', marginBottom: '8px' }}>
                {step === 1 && 'Informations de base'}
                {step === 2 && 'Votre identité'}
                {step === 3 && 'À propos de vous'}
                {step === 4 && 'Centres d\'intérêt'}
                {step === 5 && 'Photo de profil'}
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                {step === 1 && 'Créez votre compte'}
                {step === 2 && 'Pour mieux vous connaître'}
                {step === 3 && 'Parlez-nous de vous'}
                {step === 4 && 'Ce qui vous passionne'}
                {step === 5 && 'Faites bonne impression'}
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Step 1 - Informations de base */}
              {step === 1 && (
                <div>
                  <div style={{ position: 'relative', marginBottom: '16px' }}>
                    <FaEnvelope style={{ position: 'absolute', left: '18px', top: '50%', transform: 'translateY(-50%)', color: '#ff4b6e', zIndex: 2 }} />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Email"
                      style={inputStyle}
                    />
                  </div>
                  {errors.email && <span style={{ color: '#ff4b6e', fontSize: '0.7rem', marginLeft: '18px', display: 'block', marginTop: '-12px', marginBottom: '10px' }}>{errors.email}</span>}

                  <div style={{ position: 'relative', marginBottom: '16px' }}>
                    <FaLock style={{ position: 'absolute', left: '18px', top: '50%', transform: 'translateY(-50%)', color: '#ff4b6e', zIndex: 2 }} />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Mot de passe"
                      style={inputStyle}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ position: 'absolute', right: '18px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {errors.password && <span style={{ color: '#ff4b6e', fontSize: '0.7rem', marginLeft: '18px', display: 'block', marginTop: '-12px', marginBottom: '10px' }}>{errors.password}</span>}

                  <div style={{ position: 'relative', marginBottom: '16px' }}>
                    <FaUser style={{ position: 'absolute', left: '18px', top: '50%', transform: 'translateY(-50%)', color: '#ff4b6e', zIndex: 2 }} />
                    <input
                      type="text"
                      name="pseudo"
                      value={formData.pseudo}
                      onChange={handleChange}
                      placeholder="Pseudo"
                      style={inputStyle}
                    />
                  </div>
                  {errors.pseudo && <span style={{ color: '#ff4b6e', fontSize: '0.7rem', marginLeft: '18px', display: 'block', marginTop: '-12px', marginBottom: '10px' }}>{errors.pseudo}</span>}

                  <div style={{ display: 'flex', gap: '15px' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <FaUser style={{ position: 'absolute', left: '18px', top: '50%', transform: 'translateY(-50%)', color: '#ff4b6e', zIndex: 2 }} />
                      <input
                        type="text"
                        name="prenom"
                        value={formData.prenom}
                        onChange={handleChange}
                        placeholder="Prénom"
                        style={inputStyle}
                      />
                    </div>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <FaUser style={{ position: 'absolute', left: '18px', top: '50%', transform: 'translateY(-50%)', color: '#ff4b6e', zIndex: 2 }} />
                      <input
                        type="text"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        placeholder="Nom"
                        style={inputStyle}
                      />
                    </div>
                  </div>
                  {(errors.prenom || errors.nom) && <span style={{ color: '#ff4b6e', fontSize: '0.7rem', marginLeft: '18px', display: 'block', marginTop: '8px' }}>{errors.prenom || errors.nom}</span>}
                </div>
              )}

              {/* Step 2 - Détails personnels */}
              {step === 2 && (
                <div>
                  <div style={{ display: 'flex', gap: '15px' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <FaVenusMars style={{ position: 'absolute', left: '18px', top: '50%', transform: 'translateY(-50%)', color: '#ff4b6e', zIndex: 2 }} />
                      <select
                        name="genre"
                        value={formData.genre}
                        onChange={handleChange}
                        style={inputStyle}
                      >
                        <option value="">Genre</option>
                        <option value="homme">Homme</option>
                        <option value="femme">Femme</option>
                        <option value="non-binaire">Non-binaire</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <FaHeart style={{ position: 'absolute', left: '18px', top: '50%', transform: 'translateY(-50%)', color: '#ff4b6e', zIndex: 2 }} />
                      <select
                        name="orientation"
                        value={formData.orientation}
                        onChange={handleChange}
                        style={inputStyle}
                      >
                        <option value="">Orientation</option>
                        <option value="hetero">Hétérosexuel</option>
                        <option value="homo">Homosexuel</option>
                        <option value="bi">Bisexuel</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>
                  </div>
                  {errors.genre && <span style={{ color: '#ff4b6e', fontSize: '0.7rem', marginLeft: '18px', display: 'block' }}>{errors.genre}</span>}
                  {errors.orientation && <span style={{ color: '#ff4b6e', fontSize: '0.7rem', marginLeft: '18px', display: 'block' }}>{errors.orientation}</span>}

                  <div style={{ marginTop: '18px' }}>
                    <label style={{ 
                      display: 'block', 
                      color: 'rgba(255,255,255,0.7)', 
                      fontSize: '0.8rem', 
                      marginBottom: '8px',
                      marginLeft: '4px',
                      fontWeight: '500'
                    }}>
                      <FaCalendar style={{ marginRight: '6px', color: '#ff4b6e' }} />
                      Date de naissance <span style={{ color: '#ff4b6e', fontSize: '0.7rem' }}>(requis)</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <FaCalendar style={{ position: 'absolute', left: '18px', top: '50%', transform: 'translateY(-50%)', color: '#ff4b6e', zIndex: 2 }} />
                      <input
                        type="date"
                        name="date_naissance"
                        value={formData.date_naissance}
                        onChange={handleChange}
                        style={inputStyle}
                      />
                    </div>
                    {errors.date_naissance && <span style={{ color: '#ff4b6e', fontSize: '0.7rem', marginLeft: '18px', display: 'block', marginTop: '5px' }}>{errors.date_naissance}</span>}
                  </div>

                  <div style={{ display: 'flex', gap: '15px', marginTop: '18px' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <FaMapMarkerAlt style={{ position: 'absolute', left: '18px', top: '50%', transform: 'translateY(-50%)', color: '#ff4b6e', zIndex: 2 }} />
                      <input
                        type="text"
                        name="ville"
                        value={formData.ville}
                        onChange={handleChange}
                        placeholder="Ville"
                        style={inputStyle}
                      />
                    </div>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <FaFlag style={{ position: 'absolute', left: '18px', top: '50%', transform: 'translateY(-50%)', color: '#ff4b6e', zIndex: 2 }} />
                      <input
                        type="text"
                        name="pays"
                        value={formData.pays}
                        onChange={handleChange}
                        placeholder="Pays"
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3 - Bio et origine */}
              {step === 3 && (
                <div>
                  <div style={{ position: 'relative', marginBottom: '18px' }}>
                    <FaQuoteRight style={{ position: 'absolute', left: '18px', top: '16px', color: '#ff4b6e', zIndex: 2 }} />
                    <textarea
                      name="bio"
                      value={formData.bio}
                      onChange={handleChange}
                      placeholder="Parlez de vous..."
                      rows="3"
                      style={{ ...inputStyle, paddingTop: '16px', minHeight: '90px', resize: 'vertical' }}
                    />
                  </div>

                  <div style={{ position: 'relative' }}>
                    <FaGlobe style={{ position: 'absolute', left: '18px', top: '50%', transform: 'translateY(-50%)', color: '#ff4b6e', zIndex: 2 }} />
                    <select name="origine" value={formData.origine} onChange={handleChange} style={inputStyle}>
                      <option value="">Origine (optionnel)</option>
                      <option value="Française">Française</option>
                      <option value="Antillaise">Antillaise</option>
                      <option value="Africaine">Africaine</option>
                      <option value="Asiatique">Asiatique</option>
                      <option value="Métisse">Métisse</option>
                      <option value="Européenne">Européenne</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Step 4 - Centres d'intérêt */}
              {step === 4 && (
                <div>
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '10px', 
                    maxHeight: '320px', 
                    overflowY: 'auto',
                    padding: '10px',
                    background: 'rgba(0,0,0,0.2)',
                    borderRadius: '20px'
                  }}>
                    {interests.map(interest => (
                      <div
                        key={interest.id}
                        onClick={() => toggleInterest(interest.id)}
                        style={{
                          padding: '10px 18px',
                          background: selectedInterests.includes(interest.id) ? 'linear-gradient(135deg, #ff4b6e, #ff8a5c)' : '#2a2a3a',
                          border: selectedInterests.includes(interest.id) ? 'none' : '1px solid #ff4b6e',
                          borderRadius: '30px',
                          color: 'white',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <span style={{ fontSize: '1rem' }}>{interest.icon}</span>
                        <span>{interest.nom}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ textAlign: 'center', marginTop: '15px' }}>
                    <span style={{ color: '#ff8a5c', fontSize: '0.75rem' }}>
                      {selectedInterests.length} sélectionné{selectedInterests.length > 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              )}

              {/* Step 5 - Photo */}
              {step === 5 && (
                <div>
                  <div style={{ textAlign: 'center', margin: '10px 0 20px' }}>
                    <div 
                      onClick={() => avatarInputRef.current?.click()}
                      style={{
                        width: '140px',
                        height: '140px',
                        margin: '0 auto 20px',
                        borderRadius: '50%',
                        border: '3px solid #ff4b6e',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        position: 'relative',
                        background: '#2a2a3a',
                        boxShadow: '0 0 20px rgba(255,75,110,0.3)',
                        transition: 'all 0.3s'
                      }}
                    >
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                          <FaCamera style={{ fontSize: '2.2rem', color: '#ff4b6e' }} />
                          <span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.6)' }}>Ajouter</span>
                        </div>
                      )}
                      <div style={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        bottom: 0, 
                        background: 'rgba(0,0,0,0.5)', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        opacity: 0, 
                        transition: 'opacity 0.3s', 
                        borderRadius: '50%' 
                      }}>
                        <FaCamera style={{ fontSize: '1.6rem', color: 'white' }} />
                      </div>
                    </div>
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      style={{ display: 'none' }}
                    />
                    <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>JPG, PNG • Max 5MB</p>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div style={{ display: 'flex', gap: '15px', marginTop: '25px' }}>
                {step > 1 && (
                  <button type="button" onClick={handlePrev} style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: '#2a2a3a', 
                    color: 'white', 
                    border: '1px solid #ff4b6e', 
                    borderRadius: '30px', 
                    fontSize: '0.9rem', 
                    fontWeight: '500', 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: '8px',
                    transition: 'all 0.3s'
                  }}>
                    <FaArrowLeft /> Retour
                  </button>
                )}
                {step < 5 ? (
                  <button type="button" onClick={handleNext} style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: 'linear-gradient(135deg, #ff4b6e, #ff8a5c)', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '30px', 
                    fontSize: '0.9rem', 
                    fontWeight: '500', 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: '8px',
                    transition: 'all 0.3s',
                    boxShadow: '0 5px 15px rgba(255,75,110,0.3)'
                  }}>
                    Continuer {step < 5 ? (<FaArrowRight />):''}
                  </button>
                ) : (
                  <button type="submit" disabled={loading} style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: 'linear-gradient(135deg, #ff4b6e, #ff8a5c)', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '30px', 
                    fontSize: '0.9rem', 
                    fontWeight: '500', 
                    cursor: loading ? 'not-allowed' : 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: '8px',
                    opacity: loading ? 0.6 : 1,
                    transition: 'all 0.3s'
                  }}>
                    {loading ? <div style={{ width: '18px', height: '18px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div> : 'Créer mon compte'}
                  </button>
                )}
              </div>
            </form>

            <div style={{ textAlign: 'center', marginTop: '25px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
              <p>Déjà un compte ? <Link to="/login" style={{ color: '#ff4b6e', textDecoration: 'none', fontWeight: '500' }}>Se connecter</Link></p>
            </div>
          </div>
        </div>
     
     
      </div>

      <style>{`
        @keyframes zoomIn {
          from { transform: scale(1); }
          to { transform: scale(1.1); }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Responsive - Masquer la section gauche sur mobile */
        @media (max-width: 992px) {
          .register-brand-left {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Register;